import { Injectable, TransferState, makeStateKey, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class TransferStateService {
    private transferState = inject(TransferState);

    /**
     * Wraps an Observable with Transfer State caching
     * On server: fetches data and stores in transfer state
     * On client: uses cached data from server if available, otherwise fetches
     */
    useTransferState<T>(key: string, observableFactory: () => Observable<T>): Observable<T> {
        const stateKey = makeStateKey<T>(key);

        // Check if data exists in transfer state (from SSR)
        if (this.transferState.hasKey(stateKey)) {
            const cachedData = this.transferState.get(stateKey, null as any);
            // Remove from transfer state to free memory
            this.transferState.remove(stateKey);
            return of(cachedData);
        }

        // Fetch data and store in transfer state for hydration
        return observableFactory().pipe(
            tap(data => {
                this.transferState.set(stateKey, data);
            })
        );
    }

    /**
     * Check if a key exists in transfer state
     */
    hasKey(key: string): boolean {
        const stateKey = makeStateKey(key);
        return this.transferState.hasKey(stateKey);
    }

    /**
     * Get data from transfer state
     */
    get<T>(key: string, defaultValue?: T): T | undefined {
        const stateKey = makeStateKey<T>(key);
        return this.transferState.get(stateKey, defaultValue as any);
    }

    /**
     * Set data in transfer state
     */
    set<T>(key: string, value: T): void {
        const stateKey = makeStateKey<T>(key);
        this.transferState.set(stateKey, value);
    }

    /**
     * Remove data from transfer state
     */
    remove(key: string): void {
        const stateKey = makeStateKey(key);
        this.transferState.remove(stateKey);
    }
}